import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  ChevronLeft,
  ChevronRight,
  Printer,
  Droplets,
  Shield,
  CheckCircle2,
  Building2,
  Target,
  Zap,
  Award,
  Phone,
  Mail,
  MapPin,
  Hospital,
  Clock,
  DollarSign,
  Users,
  BadgeCheck,
  ArrowLeft,
  Star,
  Play,
  FlaskConical,
  Microscope,
  Activity,
  FileText,
  Settings,
  AlertTriangle,
  CircleCheck,
  Gauge,
  Server,
  Landmark,
  HeartPulse,
  Beaker,
  RefreshCw,
  Download,
  Thermometer,
  Wrench,
  HardHat,
  CircuitBoard,
  ShoppingBag
} from "lucide-react";
import { Button } from "@/components/ui/button";

// Brand Assets
const STERILE_MATE_LOGO = "/logos/sterilemate-white.png?v=4";
const GEORGE_IMAGE = "https://customer-assets.emergentagent.com/job_service-widgets/artifacts/n8vnfz06_Gemini_Generated_Image_ntzefxntzefxntze%20%281%29.png";

// Water Purification Images - ACTUAL STERILE MATE PRODUCT IMAGES
const WATER_IMAGES = {
  // TRITTON RO System actual images from Sterile Mate
  trittonSystem: "https://sterilemate.com/wp-content/uploads/2025/10/roz-ixc-series-tritton.webp",
  trittonLogo: "https://sterilemate.com/wp-content/uploads/2025/10/triton-ixc-logo.webp",
  tdsIcon: "https://sterilemate.com/wp-content/uploads/2025/10/icon-tds.webp",
  lowIcon: "https://sterilemate.com/wp-content/uploads/2025/10/icon-low.webp",
  flushIcon: "https://sterilemate.com/wp-content/uploads/2025/10/icon-flush.webp",
  mainPumpIcon: "https://sterilemate.com/wp-content/uploads/2025/10/icon-main-pump.webp",
  fullIcon: "https://sterilemate.com/wp-content/uploads/2025/10/icon-full.webp",
  monitorDisplay: "https://sterilemate.com/wp-content/uploads/2025/10/monitor.webp",
  // Dialysis Room Design AutoCAD Renderings
  dialysisRoom2D: "https://static.prod-images.emergentagent.com/jobs/6b00dc0f-3fbc-4d33-80da-2aa733a89314/images/1d8de3d4f2f04ad4205e799840ac73ce4d3c395a2baf43b79c99f65622ff98f2.png",
  dialysisRoom3D: "https://static.prod-images.emergentagent.com/jobs/6b00dc0f-3fbc-4d33-80da-2aa733a89314/images/764e6963813aa27e0c7c11d78d5e45de4c7bb398b1ed775c531488d65d1dd10a.png",
  // Stock images for general use
  labTesting: "https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?w=1200&h=800&fit=crop",
  hospital: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1200&h=800&fit=crop",
  sterileProcessing: "https://images.unsplash.com/photo-1586528243231-474b0f741873?w=1200&h=800&fit=crop",
  dialysis: "https://images.unsplash.com/photo-1664902265139-934219cee42f?w=1200&h=800&fit=crop",
  technician: "https://images.unsplash.com/photo-1581595220892-b0739db3ba8c?w=1200&h=800&fit=crop",
  labEquipment: "https://images.unsplash.com/photo-1616996691748-3f5f78093ab0?w=1200&h=800&fit=crop",
  diTank: "https://images.unsplash.com/photo-1720670996713-98e074aff7be?w=1200&h=800&fit=crop"
};

// External links for Water Purification products
const WATER_LINKS = {
  mainPage: "https://sterilemate.com/healthcare-water-purification/",
  commercialRO: "https://sterilemate.com/commercial-reverse-osmosis-water-purification-systems/"
};

// AmeriWater Product PDF Downloads
const AMERIWATER_PDFS = {
  mediQA: "https://customer-assets.emergentagent.com/job_679aa064-a1c3-435f-9d63-e85386ec1c5c/artifacts/jukvtufy_MediQA_Heat_Disinfect_RO.pdf",
  heatsan: "https://customer-assets.emergentagent.com/job_679aa064-a1c3-435f-9d63-e85386ec1c5c/artifacts/ep4sumnq_Heatsan_Heat_Disinfection_System.pdf",
  postTreatment: "https://customer-assets.emergentagent.com/job_679aa064-a1c3-435f-9d63-e85386ec1c5c/artifacts/76c4dha4_D-357-01-Post-Treatment-Rack.pdf"
};

export default function WaterPurificationDemoPage() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const deckRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handlePrint = () => {
    window.print();
  };

  const slides = [
    // Slide 1: Title
    {
      id: "title",
      content: (
        <div className="h-full flex flex-col items-center justify-center text-center bg-gradient-to-br from-blue-900 via-cyan-900 to-slate-900 text-white p-4 sm:p-8 md:p-12">
          <img src={STERILE_MATE_LOGO} alt="AskIsaac Inc" className="w-24 sm:w-36 md:w-48 h-auto mb-4 sm:mb-6 md:mb-8" />
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/20 border border-cyan-500/30 rounded-full mb-4">
            <Droplets className="w-4 h-4 text-cyan-400" />
            <span className="text-cyan-300 text-sm font-semibold">AAMI ST108 Compliant Solutions</span>
          </div>
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold mb-2 sm:mb-4">Water Purification Systems</h1>
          <p className="text-base sm:text-xl md:text-2xl text-cyan-300 mb-4 sm:mb-6 md:mb-8">Critical Water for Healthcare Excellence</p>
          <div className="w-16 sm:w-20 md:w-24 h-1 bg-gradient-to-r from-blue-500 to-cyan-500 mb-4 sm:mb-6 md:mb-8" />
          <p className="text-sm sm:text-lg md:text-xl text-slate-300">Sterile Mate Water Purification Solutions</p>
          <div className="absolute bottom-2 sm:bottom-4 md:bottom-8 text-xs sm:text-sm text-slate-500">
            Trusted by Government & Large Healthcare Organizations
          </div>
        </div>
      )
    },
    // Slide 2: Who We Are
    {
      id: "who-we-are",
      content: (
        <div className="h-full flex flex-col bg-white text-slate-800 p-3 sm:p-6 md:p-12 overflow-y-auto">
          <h2 className="text-lg sm:text-2xl md:text-3xl font-bold text-blue-700 mb-3 sm:mb-6 md:mb-8 flex items-center gap-2 sm:gap-3">
            <Building2 className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-blue-600 flex-shrink-0" />
            Who We Are
          </h2>
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
            <div className="space-y-4">
              <p className="text-sm sm:text-base md:text-lg text-slate-700 leading-relaxed">
                <strong className="text-blue-700">Sterile Mate LLC</strong> delivers <strong className="text-cyan-600">world-class water purification technology</strong> to hospitals, dialysis centers, and sterile processing departments nationwide.
              </p>
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                <h3 className="font-bold text-blue-800 mb-3 flex items-center gap-2">
                  <Award className="w-5 h-5 text-blue-600" />
                  Our Credentials
                </h3>
                <ul className="text-sm text-slate-700 space-y-2">
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-blue-600" /> Healthcare Water Purification Experts</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-blue-600" /> AAMI ST108 Compliance Experts</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-blue-600" /> ISO 17025 Accredited Testing Partners</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-blue-600" /> Government Contract Experience</li>
                </ul>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-cyan-100 rounded-xl p-3 text-center border border-cyan-200">
                  <p className="text-2xl sm:text-3xl font-bold text-cyan-600">$1M+</p>
                  <p className="text-xs sm:text-sm text-slate-600">Installations</p>
                </div>
                <div className="bg-blue-100 rounded-xl p-3 text-center border border-blue-200">
                  <p className="text-2xl sm:text-3xl font-bold text-blue-600">6+</p>
                  <p className="text-xs sm:text-sm text-slate-600">Active Systems</p>
                </div>
              </div>
            </div>
            <a 
              href={WATER_LINKS.commercialRO}
              target="_blank"
              rel="noopener noreferrer"
              className="relative rounded-xl overflow-hidden shadow-lg block hover:ring-2 hover:ring-cyan-400 transition-all"
            >
              <img 
                src={WATER_IMAGES.trittonSystem} 
                alt="Water purification system" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-blue-900/60 to-transparent flex items-end p-4">
                <p className="text-white font-semibold text-sm">TRITTON IXC™ RO/DI Systems</p>
              </div>
            </a>
          </div>
        </div>
      )
    },
    // Slide 3: Government & Enterprise Clients
    {
      id: "clients",
      content: (
        <div className="h-full flex flex-col bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white p-3 sm:p-6 md:p-12 overflow-y-auto">
          <h2 className="text-lg sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-6 flex items-center gap-2 sm:gap-3">
            <Landmark className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-amber-400 flex-shrink-0" />
            Trusted by Government & Healthcare Leaders
          </h2>
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <div className="space-y-4">
              <div className="bg-amber-900/30 border border-amber-500/30 rounded-xl p-4">
                <h3 className="font-bold text-amber-400 mb-3 flex items-center gap-2">
                  <Landmark className="w-5 h-5" />
                  Government Contracts
                </h3>
                <p className="text-amber-100 text-sm mb-3">Awarded competitive bids for federal and state healthcare facilities requiring the highest standards of water purification.</p>
                <ul className="text-xs text-amber-200 space-y-1">
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-amber-400" /> State of Texas RO System Contract</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-amber-400" /> Federal Healthcare System Partnership</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-amber-400" /> Competitive Bid Process Winner</li>
                </ul>
              </div>
              <div className="bg-blue-900/30 border border-blue-500/30 rounded-xl p-4">
                <h3 className="font-bold text-blue-400 mb-3 flex items-center gap-2">
                  <Hospital className="w-5 h-5" />
                  Large Healthcare Systems
                </h3>
                <p className="text-blue-100 text-sm mb-3">Multi-facility contracts serving major public health systems with comprehensive water purification solutions.</p>
                <ul className="text-xs text-blue-200 space-y-1">
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-blue-400" /> Major Public Health System (3 contracts)</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-blue-400" /> Texas Medical Center Facilities</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-blue-400" /> Multi-site SPD Installations</li>
                </ul>
              </div>
            </div>
            <div className="bg-white/5 rounded-xl p-5 border border-slate-700">
              <h3 className="font-bold text-lg mb-4 text-center text-cyan-300">Installation Track Record</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-4 p-3 bg-cyan-500/20 rounded-lg">
                  <div className="w-12 h-12 bg-cyan-500 rounded-lg flex items-center justify-center">
                    <Server className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-white">SPD Water Systems</p>
                    <p className="text-cyan-300 text-sm">Sterile Processing Departments</p>
                  </div>
                  <div className="ml-auto text-2xl font-bold text-cyan-400">3+</div>
                </div>
                <div className="flex items-center gap-4 p-3 bg-blue-500/20 rounded-lg">
                  <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                    <Beaker className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-white">Laboratory Systems</p>
                    <p className="text-blue-300 text-sm">Clinical & Research Labs</p>
                  </div>
                  <div className="ml-auto text-2xl font-bold text-blue-400">2+</div>
                </div>
                <div className="flex items-center gap-4 p-3 bg-violet-500/20 rounded-lg">
                  <div className="w-12 h-12 bg-violet-500 rounded-lg flex items-center justify-center">
                    <HeartPulse className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-white">Dialysis Water</p>
                    <p className="text-violet-300 text-sm">Hemodialysis Centers</p>
                  </div>
                  <div className="ml-auto text-2xl font-bold text-violet-400">1+</div>
                </div>
              </div>
              <div className="mt-4 text-center">
                <p className="text-slate-400 text-xs">All installations meet or exceed AAMI standards</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    // Slide 4: The Challenge
    {
      id: "challenge",
      content: (
        <div className="h-full flex flex-col bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white p-3 sm:p-6 md:p-12 overflow-y-auto">
          <h2 className="text-lg sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-6 md:mb-8 flex items-center gap-2 sm:gap-3">
            <Target className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-amber-400 flex-shrink-0" />
            The Critical Challenge
          </h2>
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <div className="space-y-4">
              <div className="bg-red-900/30 border border-red-500/30 rounded-xl p-4">
                <h3 className="font-bold text-red-400 mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  AAMI ST108 Compliance
                </h3>
                <p className="text-slate-300 text-sm">New standards require specific water quality for sterile processing. Non-compliance risks patient safety and regulatory penalties.</p>
              </div>
              <div className="bg-amber-900/30 border border-amber-500/30 rounded-xl p-4">
                <h3 className="font-bold text-amber-400 mb-2 flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />
                  Over-Specced Systems
                </h3>
                <p className="text-slate-300 text-sm">Traditional vendors push expensive solutions. Many facilities pay for capacity they don&apos;t need.</p>
              </div>
              <div className="bg-violet-900/30 border border-violet-500/30 rounded-xl p-4">
                <h3 className="font-bold text-violet-400 mb-2 flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Complex Installations
                </h3>
                <p className="text-slate-300 text-sm">Water system projects can take months. Delays impact OR schedules and patient care.</p>
              </div>
            </div>
            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
              <h3 className="font-bold text-xl mb-4 text-center">Water Quality Matters</h3>
              <div className="space-y-4">
                <div className="bg-cyan-900/30 rounded-lg p-4">
                  <h4 className="font-semibold text-cyan-400 mb-2">Sterile Processing (SPD)</h4>
                  <p className="text-slate-400 text-sm">Improper water = instrument corrosion, sterilization failures, and costly re-processing.</p>
                </div>
                <div className="bg-blue-900/30 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-400 mb-2">Dialysis Centers</h4>
                  <p className="text-slate-400 text-sm">Water quality directly impacts patient outcomes. Strict AAMI standards must be met continuously.</p>
                </div>
                <div className="bg-violet-900/30 rounded-lg p-4">
                  <h4 className="font-semibold text-violet-400 mb-2">Laboratory Operations</h4>
                  <p className="text-slate-400 text-sm">Test accuracy depends on ultra-pure water. Contaminants compromise results.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    // Slide 5: Our Solution - TRITTON IXC Systems
    {
      id: "solution",
      content: (
        <div className="h-full flex flex-col bg-gradient-to-br from-blue-900 via-cyan-900 to-slate-900 text-white p-3 sm:p-6 md:p-12 overflow-y-auto">
          <h2 className="text-lg sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-6 md:mb-8 flex items-center gap-2 sm:gap-3">
            <Zap className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-cyan-400 flex-shrink-0" />
            TRITTON IXC™ RO Systems
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-cyan-200 mb-6">
            Commercial reverse osmosis systems designed for healthcare — simple operation, minimal footprint, AAMI compliant.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 flex-1">
            <div className="space-y-4">
              <div className="bg-white/10 backdrop-blur rounded-xl p-4 border border-blue-500/30">
                <h3 className="font-bold text-blue-400 mb-3 flex items-center gap-2">
                  <Server className="w-5 h-5" />
                  System Specifications
                </h3>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="bg-blue-900/50 rounded p-2">
                    <p className="text-blue-300">Production</p>
                    <p className="text-white font-bold">2,100 - 15,600 GPD</p>
                  </div>
                  <div className="bg-blue-900/50 rounded p-2">
                    <p className="text-blue-300">Recovery Rate</p>
                    <p className="text-white font-bold">50-60%</p>
                  </div>
                  <div className="bg-blue-900/50 rounded p-2">
                    <p className="text-blue-300">Membrane</p>
                    <p className="text-white font-bold">4&quot; x 40&quot; Elements</p>
                  </div>
                  <div className="bg-blue-900/50 rounded p-2">
                    <p className="text-blue-300">Power</p>
                    <p className="text-white font-bold">220V - 1 PH</p>
                  </div>
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-4 border border-cyan-500/30">
                <h3 className="font-bold text-cyan-400 mb-3">Key Features</h3>
                <ul className="text-xs text-cyan-200 space-y-2">
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-cyan-400" /> Zero programming required - plug & play</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-cyan-400" /> TDS digital display for water quality</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-cyan-400" /> Low pressure safety switch</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-cyan-400" /> Membrane flush activation</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-cyan-400" /> Full tank indicator</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-cyan-400" /> Stainless steel 304 housing</li>
                </ul>
              </div>
            </div>
            <a 
              href={WATER_LINKS.commercialRO}
              target="_blank"
              rel="noopener noreferrer"
              className="relative rounded-xl overflow-hidden block hover:ring-2 hover:ring-cyan-400 transition-all"
            >
              <img src={WATER_IMAGES.trittonSystem} alt="TRITTON IXC RO System" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-blue-900/80 via-blue-900/20 to-transparent flex flex-col justify-end p-4">
                <h4 className="text-white font-bold text-lg mb-2">TRITTON IXC™ Series</h4>
                <p className="text-blue-200 text-sm">Plug & play operation. Simply connect and turn on!</p>
              </div>
            </a>
          </div>
        </div>
      )
    },
    // Slide 6: DI Tank Exchange Service
    {
      id: "di-exchange",
      content: (
        <div className="h-full flex flex-col bg-white text-slate-800 p-3 sm:p-6 md:p-12 overflow-y-auto">
          <h2 className="text-lg sm:text-2xl md:text-3xl font-bold text-blue-700 mb-3 sm:mb-6 flex items-center gap-2 sm:gap-3">
            <RefreshCw className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-blue-600 flex-shrink-0" />
            DI Tank Exchange Service
          </h2>
          <p className="text-slate-600 mb-4">Cost-effective deionization with hassle-free tank exchange program for healthcare facilities.</p>
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <div className="space-y-4">
              <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
                <h3 className="font-bold text-blue-800 mb-3">How It Works</h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm flex-shrink-0">1</div>
                    <div>
                      <p className="font-semibold text-slate-800">Initial Setup</p>
                      <p className="text-slate-600 text-sm">We install DI tanks at your facility</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm flex-shrink-0">2</div>
                    <div>
                      <p className="font-semibold text-slate-800">Scheduled Exchange</p>
                      <p className="text-slate-600 text-sm">Regular tank swaps on your schedule</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm flex-shrink-0">3</div>
                    <div>
                      <p className="font-semibold text-slate-800">Quality Guaranteed</p>
                      <p className="text-slate-600 text-sm">Fresh tanks, certified water quality</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-cyan-50 rounded-xl p-5 border border-cyan-200">
                <h3 className="font-bold text-cyan-800 mb-3">Benefits</h3>
                <ul className="text-sm text-slate-700 space-y-2">
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-cyan-600" /> No capital investment required</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-cyan-600" /> Predictable monthly costs</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-cyan-600" /> Emergency response available</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-cyan-600" /> No maintenance headaches</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-cyan-600" /> Water quality documentation</li>
                </ul>
              </div>
            </div>
            <div className="relative rounded-xl overflow-hidden shadow-lg">
              <img src={WATER_IMAGES.diTank} alt="DI Tank Exchange" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-blue-900/70 to-transparent flex items-end p-4">
                <div>
                  <p className="text-white font-bold text-lg">DI Tank Exchange</p>
                  <p className="text-blue-200 text-sm">Fresh tanks delivered, spent tanks removed</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    // Slide 7: AAMI ST108 Compliance
    {
      id: "aami",
      content: (
        <div className="h-full flex flex-col bg-white text-slate-800 p-3 sm:p-6 md:p-12 overflow-y-auto">
          <h2 className="text-lg sm:text-2xl md:text-3xl font-bold text-blue-700 mb-3 sm:mb-6 md:mb-8 flex items-center gap-2 sm:gap-3">
            <Shield className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-blue-600 flex-shrink-0" />
            AAMI ST108 Compliance Made Simple
          </h2>
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <div className="space-y-4">
              <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
                <h3 className="font-bold text-blue-800 mb-3">What is AAMI ST108?</h3>
                <p className="text-slate-700 text-sm mb-4">
                  ANSI/AAMI ST108:2023 establishes water quality requirements for processing medical devices. It specifies testing protocols, acceptable limits, and documentation requirements for healthcare facilities.
                </p>
                <div className="bg-white rounded-lg p-4 border border-blue-100">
                  <h4 className="font-semibold text-blue-700 mb-2">Key Requirements</h4>
                  <ul className="text-xs text-slate-600 space-y-1">
                    <li>• Critical water for final rinse processing</li>
                    <li>• Utility water standards for washing</li>
                    <li>• Regular monitoring and testing</li>
                    <li>• Documentation and record keeping</li>
                  </ul>
                </div>
              </div>
              <div className="bg-cyan-50 rounded-xl p-5 border border-cyan-200">
                <h3 className="font-bold text-cyan-800 mb-3">We Make It Easy</h3>
                <ul className="text-sm text-slate-700 space-y-2">
                  <li className="flex items-start gap-2">
                    <CircleCheck className="w-5 h-5 text-cyan-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Assessment:</strong> Free water quality analysis</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CircleCheck className="w-5 h-5 text-cyan-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Design:</strong> Right-sized system recommendation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CircleCheck className="w-5 h-5 text-cyan-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Install:</strong> Professional installation & training</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CircleCheck className="w-5 h-5 text-cyan-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Support:</strong> Ongoing testing & maintenance</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="bg-gradient-to-br from-blue-900 to-cyan-900 rounded-xl p-6 text-white">
              <h3 className="font-bold text-xl mb-4 text-center">Water Quality Parameters</h3>
              <div className="space-y-3">
                <div className="bg-white/10 rounded-lg p-3">
                  <div className="flex justify-between items-center">
                    <span className="text-cyan-200">Conductivity</span>
                    <span className="font-mono text-cyan-400">≤10 µS/cm</span>
                  </div>
                </div>
                <div className="bg-white/10 rounded-lg p-3">
                  <div className="flex justify-between items-center">
                    <span className="text-cyan-200">Total Hardness</span>
                    <span className="font-mono text-cyan-400">≤10 ppm</span>
                  </div>
                </div>
                <div className="bg-white/10 rounded-lg p-3">
                  <div className="flex justify-between items-center">
                    <span className="text-cyan-200">pH Level</span>
                    <span className="font-mono text-cyan-400">6.0-8.0</span>
                  </div>
                </div>
                <div className="bg-white/10 rounded-lg p-3">
                  <div className="flex justify-between items-center">
                    <span className="text-cyan-200">Bacteria (CFU/mL)</span>
                    <span className="font-mono text-cyan-400">≤200</span>
                  </div>
                </div>
                <div className="bg-white/10 rounded-lg p-3">
                  <div className="flex justify-between items-center">
                    <span className="text-cyan-200">Endotoxin (EU/mL)</span>
                    <span className="font-mono text-cyan-400">≤10</span>
                  </div>
                </div>
              </div>
              <div className="mt-4 text-center text-xs text-cyan-300">
                All systems designed to exceed these standards
              </div>
            </div>
          </div>
        </div>
      )
    },
    // Slide 8: Healthcare Applications
    {
      id: "applications",
      content: (
        <div className="h-full flex flex-col bg-gradient-to-br from-slate-100 to-blue-50 text-slate-800 p-3 sm:p-6 md:p-12 overflow-y-auto">
          <h2 className="text-lg sm:text-2xl md:text-3xl font-bold text-blue-700 mb-3 sm:mb-6 md:mb-8 flex items-center gap-2 sm:gap-3">
            <Hospital className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-blue-600 flex-shrink-0" />
            Healthcare Applications
          </h2>
          <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-blue-100">
              <div className="h-32 overflow-hidden">
                <img src={WATER_IMAGES.sterileProcessing} alt="Sterile Processing" className="w-full h-full object-cover" />
              </div>
              <div className="p-4">
                <h3 className="font-bold text-blue-800 mb-2 flex items-center gap-2">
                  <Settings className="w-5 h-5 text-blue-600" />
                  Sterile Processing (SPD)
                </h3>
                <p className="text-sm text-slate-600 mb-3">Critical and utility water for instrument reprocessing per AAMI ST108.</p>
                <ul className="text-xs text-slate-500 space-y-1">
                  <li>• Final rinse water systems</li>
                  <li>• Washer-disinfector supply</li>
                  <li>• Steam generation quality</li>
                </ul>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-cyan-100">
              <div className="h-32 overflow-hidden">
                <img src={WATER_IMAGES.dialysis} alt="Dialysis Center" className="w-full h-full object-cover" />
              </div>
              <div className="p-4">
                <h3 className="font-bold text-cyan-800 mb-2 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-cyan-600" />
                  Dialysis Centers
                </h3>
                <p className="text-sm text-slate-600 mb-3">Ultra-pure water for hemodialysis that meets AAMI standards.</p>
                <ul className="text-xs text-slate-500 space-y-1">
                  <li>• Central RO systems</li>
                  <li>• Portable RO units</li>
                  <li>• Continuous monitoring</li>
                </ul>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-teal-100">
              <div className="h-32 overflow-hidden">
                <img src={WATER_IMAGES.labTesting} alt="Laboratory" className="w-full h-full object-cover" />
              </div>
              <div className="p-4">
                <h3 className="font-bold text-teal-800 mb-2 flex items-center gap-2">
                  <FlaskConical className="w-5 h-5 text-teal-600" />
                  Laboratory & Pharmacy
                </h3>
                <p className="text-sm text-slate-600 mb-3">Reagent-grade water for testing accuracy and compounding.</p>
                <ul className="text-xs text-slate-500 space-y-1">
                  <li>• Type I/II water systems</li>
                  <li>• Pharmacy compounding</li>
                  <li>• Research applications</li>
                </ul>
              </div>
            </div>
          </div>
          <div className="mt-4 bg-blue-600 rounded-xl p-4 text-white text-center">
            <p className="font-semibold">One partner for all your healthcare water needs — from assessment to ongoing support</p>
          </div>
        </div>
      )
    },
    // Slide 9: Dialysis Water Purification — AmeriWater Systems
    {
      id: "dialysis-systems",
      content: (
        <div className="h-full flex flex-col bg-gradient-to-br from-slate-900 via-blue-950 to-cyan-950 text-white p-3 sm:p-6 md:p-8 overflow-y-auto">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 md:w-10 md:h-10 bg-cyan-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <Activity className="w-5 h-5 md:w-6 md:h-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold">Dialysis Water Purification Systems</h2>
              <p className="text-cyan-300 text-xs sm:text-sm">AmeriWater — FDA 510K Registered Medical Devices</p>
            </div>
            <div className="ml-auto bg-emerald-500/20 border border-emerald-400/50 rounded-lg px-3 py-1.5 flex-shrink-0">
              <p className="text-emerald-300 text-xs font-bold">FDA 510K</p>
              <p className="text-emerald-400/70 text-[10px]">ISO 13485</p>
            </div>
          </div>
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            {/* Option 1: Direct Feed + Hot Water Disinfection */}
            <div className="bg-white/5 backdrop-blur rounded-xl p-4 border border-cyan-500/30" data-testid="direct-feed-option">
              <div className="flex items-center gap-2 mb-3">
                <Thermometer className="w-5 h-5 text-orange-400" />
                <h3 className="font-bold text-cyan-300 text-sm sm:text-base">Option 1: Direct Feed + Hot Water Disinfection</h3>
              </div>
              <div className="bg-slate-800/80 rounded-lg p-3 mb-3">
                <h4 className="font-semibold text-white text-xs mb-2">MediQA Heat Disinfect RO</h4>
                <div className="grid grid-cols-2 gap-1.5 text-[10px] sm:text-xs">
                  <div className="bg-blue-900/50 rounded p-1.5">
                    <p className="text-blue-300">Capacity</p>
                    <p className="text-white font-bold">Up to 23,040 GPD</p>
                  </div>
                  <div className="bg-blue-900/50 rounded p-1.5">
                    <p className="text-blue-300">Recovery</p>
                    <p className="text-white font-bold">50-75% (40% less waste)</p>
                  </div>
                  <div className="bg-blue-900/50 rounded p-1.5">
                    <p className="text-blue-300">Rejection</p>
                    <p className="text-white font-bold">&gt;94% Salt Rejection</p>
                  </div>
                  <div className="bg-blue-900/50 rounded p-1.5">
                    <p className="text-blue-300">Disinfection</p>
                    <p className="text-white font-bold">Automated Heat</p>
                  </div>
                </div>
              </div>
              <ul className="text-[10px] sm:text-xs text-cyan-200 space-y-1">
                <li className="flex items-start gap-1.5"><CheckCircle2 className="w-3 h-3 text-cyan-400 mt-0.5 flex-shrink-0" />Single &amp; dual pass configurations</li>
                <li className="flex items-start gap-1.5"><CheckCircle2 className="w-3 h-3 text-cyan-400 mt-0.5 flex-shrink-0" />8&quot; TFC heat disinfectable membranes</li>
                <li className="flex items-start gap-1.5"><CheckCircle2 className="w-3 h-3 text-cyan-400 mt-0.5 flex-shrink-0" />Emergency Single Pass Mode (ESPM)</li>
                <li className="flex items-start gap-1.5"><CheckCircle2 className="w-3 h-3 text-cyan-400 mt-0.5 flex-shrink-0" />Full color touchscreen with data logging</li>
              </ul>
            </div>
            {/* Option 2: Reserve Tank System */}
            <div className="bg-white/5 backdrop-blur rounded-xl p-4 border border-blue-500/30" data-testid="reserve-tank-option">
              <div className="flex items-center gap-2 mb-3">
                <Server className="w-5 h-5 text-blue-400" />
                <h3 className="font-bold text-blue-300 text-sm sm:text-base">Option 2: Reserve Tank Configuration</h3>
              </div>
              <div className="bg-slate-800/80 rounded-lg p-3 mb-3">
                <h4 className="font-semibold text-white text-xs mb-2">Heatsan Distribution Loop System</h4>
                <div className="grid grid-cols-2 gap-1.5 text-[10px] sm:text-xs">
                  <div className="bg-blue-900/50 rounded p-1.5">
                    <p className="text-blue-300">Tank Volume</p>
                    <p className="text-white font-bold">100 Gal (380L)</p>
                  </div>
                  <div className="bg-blue-900/50 rounded p-1.5">
                    <p className="text-blue-300">Hot Water</p>
                    <p className="text-white font-bold">185F / 203F Max</p>
                  </div>
                  <div className="bg-blue-900/50 rounded p-1.5">
                    <p className="text-blue-300">Heater</p>
                    <p className="text-white font-bold">9kW or 15kW</p>
                  </div>
                  <div className="bg-blue-900/50 rounded p-1.5">
                    <p className="text-blue-300">Pump</p>
                    <p className="text-white font-bold">Up to 19.3 GPM</p>
                  </div>
                </div>
              </div>
              <ul className="text-[10px] sm:text-xs text-blue-200 space-y-1">
                <li className="flex items-start gap-1.5"><CheckCircle2 className="w-3 h-3 text-blue-400 mt-0.5 flex-shrink-0" />Automated weekly heat disinfection (2-hr hold)</li>
                <li className="flex items-start gap-1.5"><CheckCircle2 className="w-3 h-3 text-blue-400 mt-0.5 flex-shrink-0" />Intelligent leak detection &amp; monitoring</li>
                <li className="flex items-start gap-1.5"><CheckCircle2 className="w-3 h-3 text-blue-400 mt-0.5 flex-shrink-0" />Stainless steel insulated tank &amp; recirculation</li>
                <li className="flex items-start gap-1.5"><CheckCircle2 className="w-3 h-3 text-blue-400 mt-0.5 flex-shrink-0" />12-month event log with USB export to Excel</li>
              </ul>
            </div>
          </div>
          {/* Post-Treatment + Compliance Footer */}
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2">
            <div className="bg-violet-500/15 rounded-lg p-2.5 border border-violet-500/30 text-center">
              <p className="font-semibold text-violet-300 text-xs">Post-Treatment Rack</p>
              <p className="text-violet-200/70 text-[10px]">DI Bypass + Endotoxin Filter + Alarm Panel</p>
            </div>
            <div className="bg-emerald-500/15 rounded-lg p-2.5 border border-emerald-500/30 text-center">
              <p className="font-semibold text-emerald-300 text-xs">ANSI/AAMI Compliant</p>
              <p className="text-emerald-200/70 text-[10px]">13959 / 26722 / 23500:2019</p>
            </div>
            <div className="bg-amber-500/15 rounded-lg p-2.5 border border-amber-500/30 text-center">
              <p className="font-semibold text-amber-300 text-xs">Health Canada Licensed</p>
              <p className="text-amber-200/70 text-[10px]">Medical Device License 90626</p>
            </div>
          </div>
        </div>
      )
    },
    // Slide 10: Turnkey Service & Downloads
    {
      id: "turnkey-downloads",
      content: (
        <div className="h-full flex flex-col bg-white text-slate-800 p-3 sm:p-6 md:p-8 overflow-y-auto">
          <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-blue-800 mb-3 flex items-center gap-2">
            <Wrench className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 flex-shrink-0" />
            Turnkey Dialysis Water System Installation
          </h2>
          <p className="text-slate-600 text-sm mb-4">Sterile Mate provides complete project delivery — from room buildout to commissioning.</p>
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Left: Turnkey Services */}
            <div className="space-y-3">
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                <h3 className="font-bold text-blue-800 mb-3 text-sm">Complete Service Scope</h3>
                <div className="space-y-2.5">
                  {[
                    { icon: FileText, title: "Room Buildout & Modifications", desc: "Purpose-built water treatment rooms with proper ventilation, drainage, and access" },
                    { icon: Zap, title: "Electrical Systems", desc: "Dedicated 3-phase power, panel upgrades, conduit runs to equipment specifications" },
                    { icon: Droplets, title: "Plumbing & Distribution", desc: "Feed water, drain, loop piping — PVC, PEX, stainless steel, PVDF options" },
                    { icon: Settings, title: "Equipment Commissioning", desc: "Full startup, membrane conditioning, water quality verification, staff training" }
                  ].map((svc, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <svc.icon className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800 text-xs">{svc.title}</p>
                        <p className="text-slate-500 text-[10px]">{svc.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200 text-center">
                <p className="text-emerald-800 font-semibold text-xs">One Contractor. One Point of Contact. Design to Turnover.</p>
              </div>
            </div>
            {/* Right: Downloads */}
            <div className="space-y-3">
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                <h3 className="font-bold text-slate-800 mb-3 text-sm flex items-center gap-2">
                  <Download className="w-4 h-4 text-blue-600" />
                  AmeriWater Product Downloads
                </h3>
                <div className="space-y-2.5">
                  <a href={AMERIWATER_PDFS.mediQA} target="_blank" rel="noopener noreferrer" data-testid="download-mediqa-pdf"
                    className="flex items-center gap-3 p-3 bg-white rounded-lg border border-blue-200 hover:border-blue-400 hover:shadow-md transition-all group">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-blue-200 transition-colors">
                      <FileText className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-800 text-xs">MediQA Heat Disinfect RO</p>
                      <p className="text-slate-500 text-[10px]">Single/dual pass RO with automated heat disinfection</p>
                    </div>
                    <Download className="w-4 h-4 text-blue-400 group-hover:text-blue-600 flex-shrink-0" />
                  </a>
                  <a href={AMERIWATER_PDFS.heatsan} target="_blank" rel="noopener noreferrer" data-testid="download-heatsan-pdf"
                    className="flex items-center gap-3 p-3 bg-white rounded-lg border border-cyan-200 hover:border-cyan-400 hover:shadow-md transition-all group">
                    <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-cyan-200 transition-colors">
                      <Thermometer className="w-5 h-5 text-cyan-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-800 text-xs">Heatsan Heat Disinfection System</p>
                      <p className="text-slate-500 text-[10px]">Distribution loop heat disinfect with 100-gal reserve tank</p>
                    </div>
                    <Download className="w-4 h-4 text-cyan-400 group-hover:text-cyan-600 flex-shrink-0" />
                  </a>
                  <a href={AMERIWATER_PDFS.postTreatment} target="_blank" rel="noopener noreferrer" data-testid="download-post-treatment-pdf"
                    className="flex items-center gap-3 p-3 bg-white rounded-lg border border-violet-200 hover:border-violet-400 hover:shadow-md transition-all group">
                    <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-violet-200 transition-colors">
                      <Server className="w-5 h-5 text-violet-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-800 text-xs">Post-Treatment Rack</p>
                      <p className="text-slate-500 text-[10px]">DI bypass, endotoxin filtration, alarm panel — CPVC heat rated</p>
                    </div>
                    <Download className="w-4 h-4 text-violet-400 group-hover:text-violet-600 flex-shrink-0" />
                  </a>
                </div>
              </div>
              <div className="bg-blue-900 rounded-xl p-4 text-white">
                <h4 className="font-bold text-sm mb-2 text-cyan-300">Why AmeriWater?</h4>
                <div className="grid grid-cols-2 gap-2 text-[10px]">
                  <div className="flex items-center gap-1.5"><BadgeCheck className="w-3 h-3 text-cyan-400" /><span>FDA 510K Registered</span></div>
                  <div className="flex items-center gap-1.5"><BadgeCheck className="w-3 h-3 text-cyan-400" /><span>ISO 13485 Certified</span></div>
                  <div className="flex items-center gap-1.5"><BadgeCheck className="w-3 h-3 text-cyan-400" /><span>ANSI/AAMI Compliant</span></div>
                  <div className="flex items-center gap-1.5"><BadgeCheck className="w-3 h-3 text-cyan-400" /><span>Health Canada Licensed</span></div>
                  <div className="flex items-center gap-1.5"><BadgeCheck className="w-3 h-3 text-cyan-400" /><span>Made in Dayton, Ohio</span></div>
                  <div className="flex items-center gap-1.5"><BadgeCheck className="w-3 h-3 text-cyan-400" /><span>40+ Years Experience</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    // Slide 11: Dialysis Room Design Capability
    {
      id: "dialysis-design",
      content: (
        <div className="h-full flex flex-col bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white p-3 sm:p-6 md:p-12 overflow-y-auto">
          <h2 className="text-lg sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-6 flex items-center gap-2 sm:gap-3">
            <FileText className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-cyan-400 flex-shrink-0" />
            Dialysis Water Treatment Room Design
          </h2>
          <p className="text-cyan-200 text-sm mb-4">Professional AutoCAD-style room layouts to ensure your water treatment system fits perfectly.</p>
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <div className="bg-white rounded-xl p-3 overflow-hidden">
              <img 
                src={WATER_IMAGES.dialysisRoom2D} 
                alt="2D Floor Plan - Dialysis Water Treatment Room" 
                className="w-full h-full object-contain rounded-lg"
              />
            </div>
            <div className="bg-white rounded-xl p-3 overflow-hidden">
              <img 
                src={WATER_IMAGES.dialysisRoom3D} 
                alt="3D Isometric View - Dialysis Water Treatment Room" 
                className="w-full h-full object-contain rounded-lg"
              />
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-cyan-500/20 rounded-lg p-3 text-center border border-cyan-500/30">
              <p className="text-2xl font-bold text-cyan-400">15&apos;x11&apos;</p>
              <p className="text-xs text-cyan-200">Room Size</p>
            </div>
            <div className="bg-blue-500/20 rounded-lg p-3 text-center border border-blue-500/30">
              <p className="text-2xl font-bold text-blue-400">3&apos;</p>
              <p className="text-xs text-blue-200">Service Clearance</p>
            </div>
            <div className="bg-teal-500/20 rounded-lg p-3 text-center border border-teal-500/30">
              <p className="text-2xl font-bold text-teal-400">CAD</p>
              <p className="text-xs text-teal-200">Professional Drawings</p>
            </div>
            <div className="bg-emerald-500/20 rounded-lg p-3 text-center border border-emerald-500/30">
              <p className="text-2xl font-bold text-emerald-400">✓</p>
              <p className="text-xs text-emerald-200">Fit Verified</p>
            </div>
          </div>
          <div className="mt-4 bg-cyan-600/30 rounded-xl p-4 border border-cyan-500/30">
            <p className="text-center text-cyan-100 text-sm">
              <strong className="text-white">Full Design Services:</strong> We provide complete room layout design, equipment placement verification, and clearance analysis for your dialysis water treatment installation.
            </p>
          </div>
        </div>
      )
    },
    // Slide 10: Water Testing Services
    {
      id: "testing",
      content: (
        <div className="h-full flex flex-col bg-gradient-to-br from-teal-900 via-cyan-900 to-slate-900 text-white p-3 sm:p-6 md:p-12 overflow-y-auto">
          <h2 className="text-lg sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-6 flex items-center gap-2 sm:gap-3">
            <Microscope className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-teal-400 flex-shrink-0" />
            ISO 17025 Accredited Water Testing
          </h2>
          <p className="text-teal-200 text-sm mb-4">Comprehensive testing services to ensure ongoing compliance and water quality.</p>
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white/10 backdrop-blur rounded-xl p-4 border border-teal-500/30 text-center">
                <Beaker className="w-8 h-8 text-teal-400 mx-auto mb-2" />
                <h4 className="font-semibold text-white text-sm">Microbiology</h4>
                <p className="text-teal-300 text-xs mt-1">Bacteria, endotoxin testing</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-4 border border-cyan-500/30 text-center">
                <FlaskConical className="w-8 h-8 text-cyan-400 mx-auto mb-2" />
                <h4 className="font-semibold text-white text-sm">Chemical Analysis</h4>
                <p className="text-cyan-300 text-xs mt-1">Full mineral panel</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-4 border border-blue-500/30 text-center">
                <Gauge className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                <h4 className="font-semibold text-white text-sm">Conductivity</h4>
                <p className="text-blue-300 text-xs mt-1">TDS & resistivity</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-4 border border-violet-500/30 text-center">
                <FileText className="w-8 h-8 text-violet-400 mx-auto mb-2" />
                <h4 className="font-semibold text-white text-sm">Documentation</h4>
                <p className="text-violet-300 text-xs mt-1">Full compliance reports</p>
              </div>
            </div>
            <div className="bg-white/5 rounded-xl p-5 border border-slate-700">
              <h3 className="font-bold text-lg mb-4">Testing Schedule Options</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-teal-500/20 rounded-lg">
                  <div>
                    <p className="font-semibold text-white">Monthly Monitoring</p>
                    <p className="text-teal-300 text-xs">Standard compliance testing</p>
                  </div>
                  <BadgeCheck className="w-6 h-6 text-teal-400" />
                </div>
                <div className="flex items-center justify-between p-3 bg-cyan-500/20 rounded-lg">
                  <div>
                    <p className="font-semibold text-white">Quarterly Analysis</p>
                    <p className="text-cyan-300 text-xs">Comprehensive water quality</p>
                  </div>
                  <BadgeCheck className="w-6 h-6 text-cyan-400" />
                </div>
                <div className="flex items-center justify-between p-3 bg-blue-500/20 rounded-lg">
                  <div>
                    <p className="font-semibold text-white">Annual Audit</p>
                    <p className="text-blue-300 text-xs">Full system evaluation</p>
                  </div>
                  <BadgeCheck className="w-6 h-6 text-blue-400" />
                </div>
              </div>
              <div className="mt-4 p-3 bg-amber-500/20 rounded-lg border border-amber-500/30">
                <p className="text-amber-200 text-xs text-center">
                  <strong className="text-amber-400">Emergency Testing:</strong> 24-48 hour turnaround available
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    // Slide 11: Why Choose Us
    {
      id: "why-us",
      content: (
        <div className="h-full flex flex-col bg-gradient-to-br from-blue-800 via-blue-900 to-slate-900 text-white p-3 sm:p-6 md:p-12 overflow-y-auto">
          <h2 className="text-lg sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-6 md:mb-8 flex items-center gap-2 sm:gap-3">
            <Award className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-amber-400 flex-shrink-0" />
            Why Facilities Choose Sterile Mate
          </h2>
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <div className="space-y-4">
              <div className="bg-white/10 backdrop-blur rounded-xl p-5 border border-blue-500/30">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-xl bg-blue-500 flex items-center justify-center flex-shrink-0">
                    <Landmark className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-blue-400 mb-1">Government Trusted</h3>
                    <p className="text-blue-200 text-sm">Awarded contracts for state and federal healthcare facilities through competitive bid processes.</p>
                  </div>
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-5 border border-cyan-500/30">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-xl bg-cyan-500 flex items-center justify-center flex-shrink-0">
                    <DollarSign className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-cyan-400 mb-1">Right-Sized Solutions</h3>
                    <p className="text-cyan-200 text-sm">We don&apos;t oversell. Get exactly what your facility needs — nothing more, nothing less.</p>
                  </div>
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-5 border border-teal-500/30">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-xl bg-teal-500 flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-teal-400 mb-1">Expert Support</h3>
                    <p className="text-teal-200 text-sm">From assessment to installation to ongoing maintenance — we&apos;re with you every step.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white/5 rounded-xl p-6 border border-white/10">
              <h3 className="font-bold text-xl mb-4 text-center text-cyan-300">Results That Matter</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-blue-500/20 rounded-lg">
                  <div className="text-3xl font-bold text-blue-400">35%</div>
                  <div className="text-blue-200 text-sm">Average savings vs. over-specced alternatives</div>
                </div>
                <div className="flex items-center gap-4 p-4 bg-cyan-500/20 rounded-lg">
                  <div className="text-3xl font-bold text-cyan-400">$1M+</div>
                  <div className="text-cyan-200 text-sm">In water systems installed</div>
                </div>
                <div className="flex items-center gap-4 p-4 bg-teal-500/20 rounded-lg">
                  <div className="text-3xl font-bold text-teal-400">100%</div>
                  <div className="text-teal-200 text-sm">AAMI ST108 compliance rate</div>
                </div>
                <div className="flex items-center gap-4 p-4 bg-emerald-500/20 rounded-lg">
                  <div className="text-3xl font-bold text-emerald-400">24/7</div>
                  <div className="text-emerald-200 text-sm">Emergency support available</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    // Slide 12: Contact
    {
      id: "contact",
      content: (
        <div className="h-full flex flex-col bg-gradient-to-br from-blue-900 via-cyan-900 to-slate-900 text-white p-3 sm:p-6 md:p-8">
          <h2 className="text-lg sm:text-xl md:text-2xl font-bold mb-2 sm:mb-4 text-center">Ready for AAMI ST108 Compliant Water?</h2>
          <div className="flex-1 flex items-center justify-center min-h-0">
            <div className="text-center max-w-lg">
              <div className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-cyan-500/30 mb-6">
                <img src={GEORGE_IMAGE} alt="George Broughton III" className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-xl object-cover mx-auto mb-3 border-2 border-cyan-400/30" />
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-0.5 sm:mb-1">George Broughton III</h3>
                <p className="text-cyan-300 text-sm sm:text-base md:text-lg mb-4">Founder, Sterile Mate LLC</p>
                <div className="space-y-2 text-xs sm:text-sm md:text-base">
                  <a href="mailto:george.broughton@sterilemate.com" className="flex items-center justify-center gap-2 hover:text-cyan-300 transition-colors">
                    <Mail className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                    <span>george.broughton@sterilemate.com</span>
                  </a>
                  <a href="tel:+18009883892" className="flex items-center justify-center gap-2 hover:text-cyan-300 transition-colors">
                    <Phone className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                    800.988.3892
                  </a>
                  <div className="flex items-center justify-center gap-2 text-slate-400 text-xs sm:text-sm">
                    <MapPin className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                    <span>Houston, TX 77067</span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <Link to="/marketplace?category=water-purification" className="bg-sky-600 hover:bg-sky-500 transition-colors rounded-xl p-4 text-center" data-testid="water-demo-closing-shop-link">
                  <ShoppingBag className="w-6 h-6 mx-auto mb-2" />
                  <p className="font-semibold text-sm">Shop Systems</p>
                </Link>
                <Link to="/aami-st108" className="bg-blue-600 hover:bg-blue-500 transition-colors rounded-xl p-4 text-center">
                  <FileText className="w-6 h-6 mx-auto mb-2" />
                  <p className="font-semibold text-sm">ST108 Guide</p>
                </Link>
                <Link to="/how-it-works" className="bg-cyan-600 hover:bg-cyan-500 transition-colors rounded-xl p-4 text-center">
                  <Play className="w-6 h-6 mx-auto mb-2" />
                  <p className="font-semibold text-sm">See How It Works</p>
                </Link>
              </div>
              <div className="mt-4 flex items-center justify-center gap-3">
                <img src={STERILE_MATE_LOGO} alt="AskIsaac" className="w-20 sm:w-24 md:w-28 h-auto opacity-50" />
                <div className="text-left">
                  <p className="text-xs text-cyan-400 font-semibold">Healthcare Water Solutions</p>
                  <p className="text-sm text-slate-300">Sterile Mate LLC</p>
                </div>
              </div>
            </div>
          </div>
          <div className="text-center text-slate-500 text-[10px] sm:text-xs pt-1 sm:pt-2">
            Government Trusted • ISO 17025 Accredited Testing • AAMI ST108 Compliant Solutions
          </div>
        </div>
      )
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Controls - Hidden when printing */}
      <div className="print:hidden sticky top-0 z-50 bg-slate-800 border-b border-slate-700 px-3 sm:px-6 py-2 sm:py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-white bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg px-2.5 sm:px-3 py-1.5 transition-colors"
              data-testid="water-demo-home-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Home</span>
            </Link>
            <Button
              onClick={prevSlide}
              variant="outline"
              size="sm"
              className="border-slate-600 text-slate-300 hover:bg-slate-700 px-2 sm:px-3"
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="hidden sm:inline ml-1">Previous</span>
            </Button>
            <span className="text-slate-400 text-xs sm:text-sm whitespace-nowrap">
              <span className="sm:hidden">{currentSlide + 1}/{slides.length}</span>
              <span className="hidden sm:inline">Slide {currentSlide + 1} of {slides.length}</span>
            </span>
            <Button
              onClick={nextSlide}
              variant="outline"
              size="sm"
              className="border-slate-600 text-slate-300 hover:bg-slate-700 px-2 sm:px-3"
            >
              <span className="hidden sm:inline mr-1">Next</span>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button
              asChild
              size="sm"
              className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-400 hover:to-blue-500 text-white font-bold px-2 sm:px-4 shadow-lg shadow-sky-500/25"
              data-testid="water-demo-shop-btn"
            >
              <Link to="/marketplace?category=water-purification">
                <ShoppingBag className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Shop Water Systems</span>
              </Link>
            </Button>
            <Button
              onClick={handlePrint}
              size="sm"
              className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 px-2 sm:px-4"
            >
              <Printer className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Print / Save as PDF</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Slide Thumbnails - Hidden on mobile and when printing */}
      <div className="print:hidden hidden sm:block bg-slate-800/50 border-b border-slate-700 px-6 py-3 overflow-x-auto">
        <div className="max-w-7xl mx-auto flex gap-2">
          {slides.map((slide, idx) => (
            <button
              key={slide.id}
              onClick={() => setCurrentSlide(idx)}
              className={`flex-shrink-0 w-16 h-10 sm:w-20 sm:h-12 rounded border-2 overflow-hidden transition-all ${
                currentSlide === idx 
                  ? 'border-cyan-500 ring-2 ring-cyan-500/50' 
                  : 'border-slate-600 hover:border-slate-500'
              }`}
            >
              <div className="w-full h-full bg-slate-700 flex items-center justify-center text-xs text-slate-400">
                {idx + 1}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Mobile Slide Dots - Visible only on mobile */}
      <div className="print:hidden sm:hidden bg-slate-800/50 border-b border-slate-700 px-3 py-2 overflow-x-auto">
        <div className="flex justify-center gap-1.5">
          {slides.map((slide, idx) => (
            <button
              key={slide.id}
              onClick={() => setCurrentSlide(idx)}
              className={`flex-shrink-0 w-2.5 h-2.5 rounded-full transition-all ${
                currentSlide === idx 
                  ? 'bg-cyan-500 scale-125' 
                  : 'bg-slate-600 hover:bg-slate-500'
              }`}
              aria-label={`Go to slide ${idx + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Current Slide View - Screen */}
      <div className="print:hidden max-w-6xl mx-auto p-2 sm:p-4 md:p-8">
        <div 
          ref={deckRef}
          className="aspect-[16/9] rounded-lg sm:rounded-2xl overflow-hidden shadow-2xl border border-slate-700"
        >
          {slides[currentSlide].content}
        </div>
      </div>

      {/* Print View - All Slides */}
      <div className="hidden print:block">
        {slides.map((slide, idx) => (
          <div 
            key={slide.id} 
            className="w-full h-screen page-break-after"
            style={{ pageBreakAfter: 'always' }}
          >
            {slide.content}
          </div>
        ))}
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            size: landscape;
            margin: 0;
          }
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .page-break-after {
            page-break-after: always;
          }
        }
      `}</style>
    </div>
  );
}
